// 手術売上モバイル page (/m/a-opesr) — 2026-07-22 新設 (PC「手術売上」タブのモバイル要約)
// - 病院全体 当月手術収入カード (前年比 + 13ヶ月 sparkline) ・ 科別手術収入ランキング (前年比)
//     → 源 = /api/query/kb_revenue の「手術小計」(入院・046在院科・公開endpoint)
// - 手術別 (Kコード) 上位 = 術式別の金額分布 (会計件数 + 総額)
//     → 源 = /api/query/surgery_rp (fact_surgery_rp・ベータendpoint) ・ beta||admin のみ表示
// - 月送り ←→ (単月)。手術件数は別ページ (/m/a-surgery) に分離。
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { authApi } from "../../lib/auth-api";
import { useMeta } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { StatPill, sentimentFromDelta, SENTIMENT_BG, SENTIMENT_RING } from "../components/StatBadge";
import { Sparkline } from "../components/Sparkline";
import { fmtByUnitMain, fmtInt } from "../../lib/format";
import type { KbRevenueResponse, SurgeryRpResponse } from "@shared/api";

type Cell = { a: number | null; py: number | null };

// 千円 → 億/万/円 に自動スケール ({value, unit})
const fmtSen = (sen: number | null): { value: string; unit: string } =>
  sen == null ? { value: "—", unit: "千円" } : fmtByUnitMain(sen * 1000, "円");
// 円 → 億/万/円 に自動スケール ({value, unit})
const fmtEn = (en: number | null): { value: string; unit: string } =>
  en == null ? { value: "—", unit: "円" } : fmtByUnitMain(en, "円");

function cellBg(pct: number | null): string {
  if (pct == null) return "";
  if (pct >= 0.10) return "bg-emerald-300";
  if (pct >= 0.05) return "bg-emerald-100";
  if (pct >= -0.05) return "";
  if (pct >= -0.10) return "bg-red-100";
  return "bg-red-300";
}

export function SurgeryRevenueMobile() {
  const { data: meta } = useMeta();
  const { data: me } = useQuery({ queryKey: ["auth", "me"], queryFn: authApi.me, retry: false, staleTime: 30_000 });
  const isBeta = !!(me?.user.beta || me?.user.is_admin);
  const [asOfMonth, setAsOfMonth] = useState<string | null>(null);

  const periodTo = asOfMonth ?? meta?.dateMax ?? "";
  const periodFrom = periodTo ? monthShift(periodTo, -23) : "";

  // 科別手術収入 (kb_revenue 手術小計・公開)
  const { data: kb, isLoading: kbLoading } = useQuery<KbRevenueResponse>({
    queryKey: ["ope-kbrev-mobile", periodFrom, periodTo],
    queryFn: () => api.kbRevenue({ periodFrom, periodTo, metric: "手術小計" }),
    placeholderData: (p) => p,
    enabled: !!meta && !!periodTo,
  });

  // 診療科 -> ym -> Cell
  const { idx, months } = useMemo(() => {
    const idx = new Map<string, Map<string, Cell>>();
    const mset = new Set<string>();
    for (const r of kb?.rows ?? []) {
      const ym = r.date.slice(0, 7);
      mset.add(ym);
      if (!idx.has(r.診療科)) idx.set(r.診療科, new Map());
      idx.get(r.診療科)!.set(ym, { a: r.実績 ?? null, py: r.前年実績 ?? null });
    }
    return { idx, months: [...mset].sort() };
  }, [kb]);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;

  const asOfYm = periodTo.slice(0, 7);
  const dispMonths = months.length >= 13 ? months.slice(-13) : months;
  const canPrev = monthShift(periodTo, -1).slice(0, 7) >= meta.dateMin.slice(0, 7);
  const canNext = monthShift(periodTo, +1).slice(0, 7) <= meta.dateMax.slice(0, 7);

  // 病院全体 (総合計) 当月手術収入
  const totalByMonth = idx.get("総合計");
  const totalCur = totalByMonth?.get(asOfYm) ?? { a: null, py: null };
  const totalYoy = (totalCur.a != null && totalCur.py != null && totalCur.py !== 0)
    ? (totalCur.a - totalCur.py) / Math.abs(totalCur.py) : null;
  const totalSent = sentimentFromDelta(totalYoy, "higher_is_better");
  const totalSpark = dispMonths.map((ym) => totalByMonth?.get(ym)?.a ?? 0);
  const totalMain = fmtSen(totalCur.a);

  // 科別ランキング (当月実績降順、 総合計・合計行は除外)
  const ranking = [...idx.entries()]
    .filter(([d]) => d !== "総合計" && !d.includes("合計"))
    .map(([dept, m]) => {
      const c = m.get(asOfYm) ?? { a: null, py: null };
      const yoy = (c.a != null && c.py != null && c.py !== 0) ? c.a / c.py : null;
      return { dept, cur: c.a, yoy };
    })
    .filter((r) => r.cur != null && r.cur !== 0)
    .sort((a, b) => (b.cur ?? 0) - (a.cur ?? 0));

  return (
    <MobileShell
      title="💴 手術売上"
      prototypeId="A"
      prototypeName="手術売上"
      asOfMonth={periodTo}
      onPrevMonth={() => setAsOfMonth(monthShift(periodTo, -1))}
      onNextMonth={() => setAsOfMonth(monthShift(periodTo, +1))}
      canPrevMonth={canPrev}
      canNextMonth={canNext}
    >
      <div className="flex items-center gap-1.5 text-[10px] text-slate-500 flex-wrap">
        <span className="bg-slate-100 px-1.5 py-0.5 rounded">入院・手術収入 / 046在院科</span>
        {kbLoading && <span className="text-slate-400">loading…</span>}
      </div>

      {/* 病院全体 当月手術収入 */}
      <section>
        <div className="text-xs text-slate-500 mb-1">{asOfYm} 病院全体 手術収入 (入院)</div>
        <div className={`p-3 rounded-lg ring-1 ${SENTIMENT_RING[totalSent]} ${SENTIMENT_BG[totalSent]}`}>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900 tabular-nums">{totalMain.value}</span>
            <span className="text-xs text-slate-400">{totalMain.unit}</span>
            <div className="ml-auto">
              <StatPill label="前年" value={totalYoy != null ? `${totalYoy >= 0 ? "+" : ""}${(totalYoy * 100).toFixed(1)}%` : "—"} sentiment={totalSent} size="xs" />
            </div>
          </div>
          {totalSpark.filter((v) => v).length > 1 && (
            <div className="mt-1 -mx-1">
              <Sparkline values={totalSpark} width={320} height={28} showLast />
            </div>
          )}
        </div>
      </section>

      {/* 科別手術収入ランキング */}
      <section>
        <h2 className="text-xs font-semibold text-slate-700 mb-1">科別手術収入 (当月) <span className="text-slate-400 font-normal">色 = 前年同月比</span></h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600">
            <div className="w-4">#</div>
            <div>診療科</div>
            <div className="text-right">当月</div>
            <div className="text-right w-12">前年比</div>
          </div>
          {ranking.length === 0 && !kbLoading && (
            <div className="px-3 py-4 text-center text-slate-400 text-xs">データなし</div>
          )}
          {ranking.map((r, i) => {
            const m = fmtSen(r.cur);
            const pct = r.yoy != null ? r.yoy - 1 : null;
            return (
              <div key={r.dept} className="grid grid-cols-[auto_1fr_auto_auto] gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0 items-center">
                <span className="text-[10px] font-mono text-slate-400 w-4 shrink-0">{i + 1}</span>
                <span className="text-slate-800 truncate">{r.dept}</span>
                <span className="text-right tabular-nums text-slate-800 whitespace-nowrap">
                  {m.value}<span className="text-[9px] text-slate-400 ml-0.5">{m.unit === "千円" ? "" : m.unit}</span>
                </span>
                <span className={`text-right tabular-nums font-semibold w-12 rounded ${cellBg(pct)}`}>
                  {r.yoy != null ? `${(r.yoy * 100).toFixed(0)}%` : "—"}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* 手術別 (Kコード) 上位 — ベータ (surgery_rp) */}
      {isBeta
        ? <SurgeryRpTop periodTo={periodTo} asOfYm={asOfYm} />
        : (
          <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
            術式 (Kコード) 別の売上内訳は PC 版 (ベータ) でご覧ください。
          </p>
        )}
    </MobileShell>
  );
}

// 手術別 (Kコード) 上位10 — 総額/合計 降順。 単月。 ベータのみ呼び出す (enabled 制御は呼び元)。
function SurgeryRpTop({ periodTo, asOfYm }: { periodTo: string; asOfYm: string }) {
  const { data, isLoading } = useQuery<SurgeryRpResponse>({
    queryKey: ["ope-surgeryrp-mobile", periodTo],
    queryFn: () => api.surgeryRp({ dateFrom: periodTo, dateTo: periodTo }),
    placeholderData: (p) => p,
  });

  const top = useMemo(() => {
    const rows = data?.rows ?? [];
    return [...rows].sort((a, b) => b.total_sum - a.total_sum).slice(0, 10);
  }, [data]);

  return (
    <section>
      <h2 className="text-xs font-semibold text-slate-700 mb-1">
        手術別 上位10 <span className="text-slate-400 font-normal">({asOfYm} 総額順)</span>
        {isLoading && <span className="ml-2 text-[9px] text-slate-400">loading…</span>}
      </h2>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600">
          <div>術式 (Kコード)</div>
          <div className="text-right w-12">件数</div>
          <div className="text-right w-16">総額</div>
        </div>
        {top.length === 0 && !isLoading && (
          <div className="px-3 py-4 text-center text-slate-400 text-xs">該当データなし</div>
        )}
        {top.map((r) => {
          const m = fmtEn(r.total_sum);
          return (
            <div key={r.kcode} className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0 items-center">
              <div className="min-w-0">
                <span className="font-mono text-[10px] text-slate-500 mr-1">{r.kcode}</span>
                <span className="text-slate-800">{r.kname}</span>
              </div>
              <span className="text-right tabular-nums text-slate-700 w-12">{fmtInt(r.n)}</span>
              <span className="text-right tabular-nums font-semibold text-slate-800 w-16 whitespace-nowrap">
                {m.value}<span className="text-[9px] text-slate-400 ml-0.5">{m.unit === "千円" ? "" : m.unit}</span>
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-1 px-1 leading-relaxed">
        総額 = 手技+材料+薬剤 (円)。件数 = 手術会計件数 (RP単位・手術台帳とは別)。診療科別・分布は PC 版へ。
      </p>
    </section>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
